import {
  AnnouncementDto,
  Configuration,
  CreateAnnouncementDto,
  DefaultApi,
  UpdateAnnouncementDto,
} from '@cratehackers/api-client';
import { config } from '../config';
import { AppIdentifiers } from '../store/slices/token-state.slice';
import { getAppStore } from '../store/storeRef';
import { openApiFetchWrapper } from '../utils/openApiFetchWrapper';

export class AnnouncementsApi {
  private static readonly MAX_IMAGE_BYTES = Math.floor(2.5 * 1024 * 1024); // 2.5MB
  private static readonly MAX_VIDEO_BYTES = Math.floor(2.5 * 1024 * 1024); // 2.5MB

  private static getAuthToken(token?: string): string {
    if (token) return token;
    const appToken = getAppStore().getState().tokenState[AppIdentifiers.App];
    return appToken && appToken.accessToken ? appToken.accessToken : '';
  }

  private static getAuthHeader(token?: string): Record<string, string> {
    const authToken = AnnouncementsApi.getAuthToken(token);
    return authToken ? { Authorization: `Bearer ${authToken}` } : {};
  }

  private static getApiClient(token?: string): DefaultApi {
    const apiConfig = new Configuration({
      basePath: config.API_BASE_URL,
      accessToken: AnnouncementsApi.getAuthToken(token),
      fetchApi: openApiFetchWrapper,
    });
    return new DefaultApi(apiConfig);
  }

  static async listActive(): Promise<AnnouncementDto[]> {
    const apiClient = AnnouncementsApi.getApiClient();
    return await apiClient.announcementControllerListActive();
  }

  static async listAll(token?: string): Promise<AnnouncementDto[]> {
    const apiClient = AnnouncementsApi.getApiClient(token);
    return await apiClient.announcementControllerListAll();
  }

  static async create(
    payload: Partial<AnnouncementDto>,
    token?: string,
  ): Promise<AnnouncementDto> {
    const apiClient = AnnouncementsApi.getApiClient(token);
    return await apiClient.announcementControllerCreate({
      createAnnouncementDto: payload as CreateAnnouncementDto,
    });
  }

  static async update(
    id: number,
    payload: Partial<AnnouncementDto>,
    token?: string,
  ): Promise<AnnouncementDto> {
    const apiClient = AnnouncementsApi.getApiClient(token);
    return await apiClient.announcementControllerUpdate({
      id: id.toString(),
      updateAnnouncementDto: payload as UpdateAnnouncementDto,
    });
  }

  static async remove(id: number, token?: string): Promise<void> {
    const apiClient = AnnouncementsApi.getApiClient(token);
    try {
      // Use permanent delete for admin-driven removal so items do not reappear.
      await apiClient.announcementControllerRemovePermanent({
        id: id.toString(),
      });
    } catch {
      // Fallback for older backends that may not expose permanent removal.
      await apiClient.announcementControllerRemove({ id: id.toString() });
    }
  }

  // Upload with progress via XHR; returns a promise that resolves with { url }
  static uploadWithProgress(
    file: File,
    onProgress?: (pct: number) => void,
    token?: string,
  ): Promise<{ url: string }> {
    const maxBytes = file.type.startsWith('video/')
      ? AnnouncementsApi.MAX_VIDEO_BYTES
      : AnnouncementsApi.MAX_IMAGE_BYTES;
    if (file.size > maxBytes) {
      const maxMb = Math.floor(maxBytes / (1024 * 1024));
      return Promise.reject(
        new Error(`File too large. Max allowed is ${maxMb}MB.`),
      );
    }

    if (!onProgress) {
      const apiClient = AnnouncementsApi.getApiClient(token);
      return apiClient.announcementControllerUpload({ file }).then((res) => {
        if (!res.url) {
          throw new Error('Upload failed: missing URL in response');
        }
        return { url: res.url };
      });
    }

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${config.API_BASE_URL}/announcements/upload`);
      if (token) {
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      } else {
        const header = AnnouncementsApi.getAuthHeader();
        if (header.Authorization) {
          xhr.setRequestHeader('Authorization', header.Authorization);
        }
      }
      xhr.onreadystatechange = () => {
        if (xhr.readyState === 4) {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              resolve(JSON.parse(xhr.responseText));
            } catch (e) {
              reject(e);
            }
          } else {
            reject(new Error(`Upload failed: ${xhr.status}`));
          }
        }
      };
      if (xhr.upload && onProgress) {
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) {
            onProgress(Math.round((e.loaded / e.total) * 100));
          }
        };
      }
      const form = new FormData();
      form.append('file', file);
      xhr.send(form);
    });
  }
}

export type { AnnouncementDto };

export default AnnouncementsApi;
